import React, { useMemo } from 'react';
import moment from 'moment';
import { showCurrency } from '../../utils';
import { Section, StatStrip, DataTable, IdChip, Pill } from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	if (amount === null || amount === undefined) {
		return '';
	}
	return `${showCurrency(currency) || ''} ${Number(amount).toLocaleString('es-UY')}`.trim();
}

function money(due, value) {
	return <span className='tw:font-mono tw:tabular-nums'>{formatMoney(due.credit?.currency, value)}</span>;
}

function CreditDues({
	rows = [],
	loading,
	error,
	expiredCredit,
	allDuesGenerated,
	nextExpirationDate,
	dueNumber,
	generationDate,
}) {
	const { t } = useLabels();

	const stats = useMemo(() => {
		const pending = rows.filter((due) => !due.paid);
		const outstandingByCurrency = new Map();
		pending.forEach((due) => {
			const key = due.credit?.currency || '—';
			outstandingByCurrency.set(key, (outstandingByCurrency.get(key) || 0) + (due.unpaidAmount || 0));
		});
		return [
			{ label: t('entity.due.plural'), value: rows.length },
			{ label: 'Pendientes', value: pending.length },
			{
				label: t('field.unpaidAmount'),
				value:
					[...outstandingByCurrency.entries()]
						.map(([currency, amount]) => formatMoney(currency, amount))
						.join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows, t]);

	const columns = [
		{ key: 'customId', header: t('entity.due'), render: (due) => <IdChip>{due.customId}</IdChip> },
		{ key: 'date', header: t('field.date'), render: (due) => formatDate(due.date) },
		{ key: 'expiration', header: t('field.expiration'), render: (due) => formatDate(due.expiration) },
		{
			key: 'interest',
			header: t('field.interest'),
			className: 'tw:text-right',
			render: (due) => money(due, due.interest),
		},
		{
			key: 'amortization',
			header: t('field.amortization'),
			className: 'tw:text-right',
			render: (due) => money(due, due.amortization),
		},
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (due) => money(due, due.amount),
		},
		{
			key: 'unpaidAmount',
			header: t('field.unpaidAmount'),
			className: 'tw:text-right',
			render: (due) => money(due, due.unpaidAmount),
		},
		{
			key: 'paid',
			header: t('field.paid'),
			render: (due) => <Pill tone={due.paid ? 'ok' : 'neutral'}>{due.paid ? 'Pagada' : 'Pendiente'}</Pill>,
		},
	];

	const note = expiredCredit
		? t('msg.creditExpired')
		: allDuesGenerated
			? t('msg.allDuesGenerated')
			: t('msg.nextExpirationTemplate')
				.replace('{date}', nextExpirationDate)
				.replace('{number}', dueNumber)
				.replace('{generationDate}', generationDate);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			{note && <div className='tw:text-[12.5px] tw:text-ink-2'>{note}</div>}
			<Section title={t('entity.due.plural')}>
				<DataTable
					columns={columns}
					rows={rows}
					loading={loading}
					error={error}
					emptyMessage='No se encontraron cuotas para este crédito.'
				/>
			</Section>
		</div>
	);
}

export default CreditDues;
